import { TaskBoard } from "./TaskBoard";
import { useTasks, useServices } from "@hq/client-react";
import type { Priority, Task, TaskStatus, TaskView } from "@hq/contracts";
import { applyTaskView, groupTasks, todayOf, type NowContext, type TaskGroup } from "@hq/core";
import type { TaskQuery } from "@hq/local-store";
import type { ReactNode } from "react";
import { formatDayHeading, isIsoDate } from "./format";
import { TaskGroupList } from "./TaskGroupList";
import { useNow } from "./useNow";
import { useProjectIndex } from "./useProjectNames";

export interface TaskListProps {
  view: TaskView;
  mode?: "list" | "board";
  /** Narrows the coarse store query to one project (`null` = Inbox) regardless of the view. */
  projectId?: string | null;
  emptyTitle?: ReactNode;
  emptyDescription?: ReactNode;
  emptyAction?: ReactNode;
  onOpenTask?: (taskId: string) => void;
  /** Makes each row's tag badges clickable. */
  onTagClick?: (tag: string) => void;
  /** Rendered above the rows; `c` focuses its input. */
  quickAdd?: ReactNode;
  /** Replaces `groupTasks` for groupings core does not know (completion day). */
  groupWith?: (tasks: Task[], now: NowContext) => TaskGroup[];
  /** Pins the clock; defaults to the shared ticking clock. */
  now?: NowContext;
  className?: string;
}

/** The coarse store query implied by a view: one project, a status set, or everything. */
export function coarseQueryFor(view: TaskView, projectId?: string | null): TaskQuery {
  const q: TaskQuery = {};
  const ids = view.filters.projectIds;
  if (projectId !== undefined) q.projectId = projectId;
  else if (ids && ids.length === 1) q.projectId = ids[0] ?? null;
  const status =
    view.filters.status ?? (view.display.showCompleted ? undefined : ["open", "someday"]);
  if (status) q.status = status;
  return q;
}

const NONE: readonly Task[] = [];

/**
 * Loads a coarse candidate set, then filters, sorts and groups it with `core` in memory.
 * Everything visible is derived during render from the live query and the clock.
 */
export function TaskList({
  view,
  mode = "list",
  projectId,
  emptyTitle,
  emptyDescription,
  emptyAction,
  onOpenTask,
  onTagClick,
  quickAdd,
  groupWith,
  now: nowOverride,
  className,
}: TaskListProps) {
  const now = useNow(nowOverride);
  const services = useServices();
  const { projects, names } = useProjectIndex();
  // Inbox and project boards show their entire workflow; date/tag constraints still apply.
  const boardScope = mode === "board" && (view.id === "inbox" || projectId !== undefined);
  const effectiveView: TaskView = boardScope
    ? {
        ...view,
        filters: { ...view.filters, status: ["open", "someday", "completed"] },
        display: { ...view.display, showCompleted: true },
      }
    : view;
  const { data } = useTasks(coarseQueryFor(effectiveView, projectId));
  const visible = applyTaskView(data ?? NONE, effectiveView, now);
  const groups = groupWith
    ? groupWith(visible, now)
    : groupTasks(visible, view, now, { projectNames: names });

  const formatGroupLabel = (group: TaskGroup) =>
    (view.groupBy === "dueDate" || view.groupBy === "plannedDate") && isIsoDate(group.label)
      ? formatDayHeading(group.label, now)
      : group.label;

  const movableGrouping =
    !groupWith && ["project", "priority", "status", "heading", "dueDate"].includes(view.groupBy);
  const moveToGroup = async (task: Task, group: TaskGroup) => {
    switch (view.groupBy) {
      case "project":
        await services.tasks.moveToProject(task.id, group.key === "unassigned" ? null : group.key);
        break;
      case "priority":
        await services.tasks.setPriority(
          task.id,
          group.key === "9-none" ? null : (group.key.split("-")[1] as Priority),
        );
        break;
      case "status":
        await services.tasks.setStatus(task.id, group.key as TaskStatus);
        break;
      case "heading":
        await services.tasks.setHeading(task.id, group.key === "~none" ? null : group.key);
        break;
      case "dueDate":
        await services.tasks.setDueDate(
          task.id,
          group.key === "~none"
            ? null
            : group.key === "1-today"
              ? todayOf(now)
              : group.key.slice(2),
        );
        break;
    }
  };

  if (mode === "board")
    return (
      <TaskBoard
        tasks={visible}
        now={now}
        quickAdd={quickAdd}
        onOpenTask={onOpenTask}
        label={view.name}
      />
    );
  return (
    <TaskGroupList
      groups={groups}
      {...(movableGrouping
        ? {
            onMoveToGroup: moveToGroup,
            canMoveToGroup: (_task: Task, group: TaskGroup) =>
              view.groupBy !== "dueDate" || group.key !== "0-overdue",
          }
        : {})}
      reorderable={view.sort.by === "manual"}
      emptyAction={emptyAction}
      now={now}
      projects={projects}
      showProject={view.display.showProject}
      density={view.display.density}
      formatGroupLabel={formatGroupLabel}
      aria-label={view.name}
      {...(onOpenTask ? { onOpenTask } : {})}
      {...(onTagClick ? { onTagClick } : {})}
      {...(quickAdd !== undefined ? { quickAdd } : {})}
      {...(emptyTitle !== undefined ? { emptyTitle } : {})}
      {...(emptyDescription !== undefined ? { emptyDescription } : {})}
      {...(className !== undefined ? { className } : {})}
    />
  );
}
